import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/format'

export interface Crumb {
  label: string
  to?: string
}

export interface PageHeaderProps {
  title: ReactNode
  /** Mono line under the title (hostnames, ids). */
  subtitle?: ReactNode
  description?: ReactNode
  crumbs?: Crumb[]
  /** Status-badge strip, under the title on phones, top-right on desktop. */
  badges?: ReactNode
  actions?: ReactNode
  className?: string
}

/** Page title block: condensed scoreboard heading, crumbs, badges, actions. */
export function PageHeader({ title, subtitle, description, crumbs, badges, actions, className }: PageHeaderProps) {
  return (
    <header className={cn('grid gap-3', className)}>
      {crumbs?.length ? (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted">
            {crumbs.map((c, i) => (
              <li key={i} className="flex min-w-0 items-center gap-1.5">
                {i > 0 ? <span aria-hidden className="text-subtle">/</span> : null}
                {c.to ? (
                  <Link to={c.to} className="hover:text-ink">
                    {c.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="truncate">
                    {c.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="break-words text-[1.875rem] font-extrabold leading-none text-ink sm:text-[2.125rem]">{title}</h1>
          {subtitle ? <div className="mt-2 break-words font-mono text-[12.5px] font-medium text-muted">{subtitle}</div> : null}
          {description ? <p className="mt-2 max-w-2xl text-[13.5px] text-muted">{description}</p> : null}
        </div>
        {badges || actions ? (
          // Both rows end at the content's right edge whether the block sits beside the title
          // or has wrapped under a long one: ml-auto keeps a wrapped block on the right instead
          // of at the row's start, where its buttons floated mid-row (#1014). Phones stack left.
          <div className="grid justify-items-start gap-2 sm:ml-auto sm:justify-items-end">
            {badges ? <div className="flex flex-wrap gap-1.5">{badges}</div> : null}
            {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
          </div>
        ) : null}
      </div>
    </header>
  )
}
